import type { Team } from '../api.js';
import styles from './TeamBadge.module.css';

/**
 * The little image beside a team's name — a national flag, or a club crest.
 *
 * Two sources because the two kinds of team genuinely have different assets,
 * not because of legacy drift:
 *
 * - `flagCode` is a short flagcdn.com key (`"br"`, `"gb-eng"`) that only
 *   national teams have. It's also the field a manual edit sets, so it wins
 *   whenever it's there.
 * - `crestUrl` is a full badge URL a live provider reported (football-data.org
 *   sends one with every fixture). Clubs have only this.
 *
 * So the fallback order is "curated flag, then whatever the feed gave us",
 * and a team with neither renders nothing rather than a broken image.
 *
 * `alt=""` throughout: the team's name is always rendered as text right next
 * to this, so announcing the crest as well would just make a screen reader
 * say the team twice.
 */
export default function TeamBadge({ team }: { team: Team | null }) {
  if (!team) return null;

  if (team.flagCode) {
    return (
      <img className={styles.badge} src={`https://flagcdn.com/24x18/${team.flagCode}.png`} alt="" />
    );
  }
  if (team.crestUrl) {
    // `contain`, unlike a flag's `cover`: a crest is usually square-ish with
    // its own transparent padding, and cropping it to a 22×16 flag box would
    // slice the badge. Letterboxing inside the same box keeps every team's
    // name starting at the same x regardless of which asset it has.
    return <img className={`${styles.badge} ${styles.crest}`} src={team.crestUrl} alt="" />;
  }
  return null;
}
